/**
 * apiClient — Axios-based HTTP client for all business API calls.
 *
 * Request interceptor: injects Authorization + X-Tenant-Slug from the auth
 * store on every request.
 *
 * Response interceptor:
 *  - 401 → silently refresh token (auth store mutex) and retry once.
 *          On second 401, clear auth and redirect to /login.
 *  - 403 step_up_required → store pending step-up data and emit a DOM event
 *          so App.vue can navigate to /auth/step-up.
 *  - All error responses are re-shaped to { status, data } for backward
 *          compatibility with existing callers in api.js.
 */
import axios from 'axios'
import { i18n } from '@/i18n.js'
import { useAuthStore } from '@/stores/auth.js'

const loginURL = import.meta.env.VITE_LOGIN_URL || '/login'
const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/'

function getCsrfToken() {
  try {
    const match = document.cookie.match(/csrftoken=([^;]+)/)
    return match ? match[1] : ''
  } catch {
    return ''
  }
}

const axiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
})

// ── Request interceptor ───────────────────────────────────────────────────
axiosInstance.interceptors.request.use((config) => {
  const csrf = getCsrfToken()
  if (csrf) config.headers['X-CSRFToken'] = csrf

  const authStore = useAuthStore()
  if (authStore.accessToken) config.headers['Authorization'] = `Bearer ${authStore.accessToken}`
  if (authStore.currentTenant?.slug) config.headers['X-Tenant-Slug'] = authStore.currentTenant.slug

  return config
})

// ── Response interceptor ──────────────────────────────────────────────────
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const authStore = useAuthStore()

    // 401 → attempt silent token refresh, then retry the original request once
    if (error.response?.status === 401 && !error.config._retry) {
      error.config._retry = true
      try {
        await authStore.refreshToken()
        error.config.headers['Authorization'] = `Bearer ${authStore.accessToken}`
        return axiosInstance(error.config)
      } catch {
        authStore.clearAll()
        window.location.href = loginURL
        const err = new Error(i18n.global.t('ui.sessionExpired'))
        err.status = 401
        err.data = { error: i18n.global.t('ui.sessionExpired') }
        return Promise.reject(err)
      }
    }

    // 403 step_up_required → notify App.vue to navigate to step-up view
    if (error.response?.status === 403 && error.response.data?.step_up_required) {
      authStore.setPendingStepUp(error.response.data)
      window.dispatchEvent(new CustomEvent('auth:step-up-required', { detail: error.response.data }))
    }

    // Normalise error shape for backward compatibility: e.status, e.data
    if (error.response) {
      const err = new Error(`HTTP ${error.response.status}`)
      err.status = error.response.status
      err.data = error.response.data
      return Promise.reject(err)
    }

    return Promise.reject(error)
  },
)

// ── Wrapper class  (preserves the existing APIClient interface) ───────────
class APIClient {
  _unwrap(response) {
    return response.status === 204 ? null : response.data
  }

  async get(endpoint, params = {}) {
    return this._unwrap(await axiosInstance.get(endpoint, { params }))
  }

  async getBlob(endpoint) {
    return this._unwrap(await axiosInstance.get(endpoint, { responseType: 'blob' }))
  }

  async post(endpoint, data = {}) {
    return this._unwrap(await axiosInstance.post(endpoint, data))
  }

  async put(endpoint, data = {}) {
    return this._unwrap(await axiosInstance.put(endpoint, data))
  }

  async patch(endpoint, data = {}) {
    return this._unwrap(await axiosInstance.patch(endpoint, data))
  }

  // FormData: pass undefined for Content-Type so Axios/browser sets multipart + boundary
  async postForm(endpoint, formData) {
    return this._unwrap(await axiosInstance.post(endpoint, formData, {
      headers: { 'Content-Type': undefined },
    }))
  }

  async patchForm(endpoint, formData) {
    return this._unwrap(await axiosInstance.patch(endpoint, formData, {
      headers: { 'Content-Type': undefined },
    }))
  }

  async delete(endpoint, data = {}) {
    return this._unwrap(await axiosInstance.delete(endpoint, { data }))
  }
}

export const apiClient = new APIClient()
export { loginURL, baseURL }
